// Nine Lives '84 - title and game-over screens.
//
// A DOM overlay over the live world. The title offers the four difficulties
// and shows the high score table; the game-over screen shows the final
// score and returns to the title.

import { DEFAULT_SETTINGS, type Settings } from '../settings';
import { Difficulty, DIFFICULTY_NAMES } from '../sim/types';
import type { HighScore, HighScores } from './highscores';

export interface StartChoice {
  difficulty: Difficulty;
}

const LEVELS: [Difficulty, string][] = [
  [Difficulty.KITTEN, 'Nine lives, a gentle alley.'],
  [Difficulty.HOUSE_CAT, 'Six lives. The way it was meant to be played.'],
  [Difficulty.TOMCAT, 'Four lives, faster brooms, meaner dogs.'],
  [Difficulty.ALLEY_CAT, 'Three lives. The fence needs a running jump.'],
];

export class TitleScreen {
  private readonly root: HTMLElement;
  private readonly body: HTMLElement;
  onStart: ((choice: StartChoice) => void) | null = null;
  /** Extra line under the subtitle (build notes, load errors). */
  note = '';
  /** The player's switches; the panel edits a copy and reports changes. */
  settings: Settings = { ...DEFAULT_SETTINGS };
  onSettings: ((s: Settings) => void) | null = null;
  private lastDifficulty = Difficulty.HOUSE_CAT;

  constructor(
    container: HTMLElement,
    private readonly highs: HighScores,
  ) {
    this.root = el('div', 'title');
    this.body = el('div', 'title-body');
    this.root.appendChild(this.body);
    container.appendChild(this.root);
    window.addEventListener('keydown', (e) => {
      if (this.root.hidden) return;
      if (e.code === 'Enter' || e.code === 'Space') {
        e.preventDefault();
        this.onStart?.({ difficulty: this.lastDifficulty });
      } else if (e.code.startsWith('Digit')) {
        const n = Number(e.code.slice(5)) - 1;
        if (n >= 0 && n < LEVELS.length) this.onStart?.({ difficulty: LEVELS[n][0] });
      }
    });
  }

  get visible(): boolean {
    return !this.root.hidden;
  }

  hide(): void {
    this.root.hidden = true;
  }

  showTitle(): void {
    this.body.replaceChildren();
    const h1 = el('h1');
    h1.textContent = "NINE LIVES '84";
    const sub = el('p', 'title-sub');
    sub.textContent =
      'An 8-bit alley caper, rebuilt in 3D. Climb the alley, raid the rooms, dodge the broom, find Clementine.';
    this.body.append(h1, sub);
    if (this.note) {
      const n = el('p', 'title-note');
      n.textContent = this.note;
      this.body.appendChild(n);
    }
    this.body.append(this.difficultyButtons(), this.settingsPanel(), this.scoreTable());
    const foot = el('p', 'title-foot');
    foot.textContent =
      'Enter or click to start · ← → run · ↑/Space jump · ↓ down · X action · P pause · / legend · S sound · R retro · Esc quit';
    const credit = el('p', 'title-credit');
    credit.textContent =
      'Inspired by Alley Cat (Bill Williams / Synapse Software, 1983; IBM PC 1984). This tribute is original code, art and music.';
    this.body.append(foot, credit);
    this.root.hidden = false;
  }

  showGameOver(score: number, level: number, difficulty: Difficulty, rank: number): void {
    this.lastDifficulty = difficulty;
    this.body.replaceChildren();
    const h1 = el('h1');
    h1.textContent = 'GAME OVER';
    const s = el('p', 'title-score');
    s.textContent = `Score ${score} · reached level ${level} · ${DIFFICULTY_NAMES[difficulty]}`;
    this.body.append(h1, s);
    if (rank > 0) {
      const r = el('p', 'title-rank');
      r.textContent = rank === 1 ? 'New high score!' : `High score #${rank}`;
      this.body.appendChild(r);
    }
    this.body.append(this.difficultyButtons('Play again'), this.settingsPanel(), this.scoreTable());
    this.root.hidden = false;
  }

  private settingsPanel(): HTMLElement {
    const box = el('div', 'title-settings');
    const check = (key: 'retro' | 'reduceMotion' | 'lowDetail' | 'music', text: string): void => {
      const label = document.createElement('label');
      label.className = 'title-check';
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.checked = this.settings[key];
      input.addEventListener('change', () => {
        this.settings = { ...this.settings, [key]: input.checked };
        this.onSettings?.(this.settings);
      });
      const span = el('span');
      span.textContent = text;
      label.append(input, span);
      box.appendChild(label);
    };
    check('retro', 'Retro: the four-colour 320x200 look (R in game)');
    check('reduceMotion', 'Reduce motion: no camera moves or shake');
    check('lowDetail', 'Low detail: no shadows or glow (phones, older graphics)');
    check('music', 'Music');
    const vol = document.createElement('label');
    vol.className = 'title-check title-volume';
    const range = document.createElement('input');
    range.type = 'range';
    range.min = '0';
    range.max = '100';
    range.value = String(Math.round(this.settings.volume * 100));
    range.addEventListener('input', () => {
      this.settings = { ...this.settings, volume: Number(range.value) / 100 };
      this.onSettings?.(this.settings);
    });
    const span = el('span');
    span.textContent = 'Volume';
    vol.append(span, range);
    box.appendChild(vol);
    return box;
  }

  private difficultyButtons(prefix = 'Play'): HTMLElement {
    const box = el('div', 'title-modes');
    LEVELS.forEach(([difficulty, desc], i) => {
      const b = document.createElement('button');
      b.className = 'title-mode';
      const k = el('span', 'title-key');
      k.textContent = String(i + 1);
      const l = el('span', 'title-label');
      l.textContent = `${prefix}: ${DIFFICULTY_NAMES[difficulty]}`;
      const d = el('span', 'title-desc');
      d.textContent = desc;
      b.append(k, l, d);
      b.addEventListener('click', () => {
        this.lastDifficulty = difficulty;
        this.onStart?.({ difficulty });
      });
      box.appendChild(b);
    });
    return box;
  }

  private scoreTable(): HTMLElement {
    const box = el('div', 'title-scores');
    const h = el('h2');
    h.textContent = 'High scores';
    box.appendChild(h);
    const list = this.highs.list;
    if (!list.length) {
      const p = el('p');
      p.textContent = 'No scores yet.';
      box.appendChild(p);
      return box;
    }
    const table = document.createElement('table');
    list.forEach((e: HighScore, i) => {
      const tr = document.createElement('tr');
      for (const text of [`${i + 1}.`, String(e.score), `L${e.level}`, e.mode]) {
        const td = document.createElement('td');
        td.textContent = text;
        tr.appendChild(td);
      }
      table.appendChild(tr);
    });
    box.appendChild(table);
    return box;
  }
}

function el(tag: string, cls?: string): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}
